if (!customElements.get('bt-signals-test')) {
  class BtSignalsTest extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.initialized = true;
      this.proxyPath = (this.dataset.proxyPath || '/apps/bt-signals').replace(/\/+$/, '');
      this.form = this.querySelector('form');
      this.guestIdInput = this.querySelector('[name="guest_id"]');
      this.status = this.querySelector('[data-status]');
      this.result = this.querySelector('[data-result]');

      if (!this.form || !this.guestIdInput || !this.status || !this.result) return;

      this.guestIdInput.value = this.getGuestId();
      this.form.addEventListener('click', (event) => this.handleClick(event));
    }

    handleClick(event) {
      const button = event.target.closest('button[data-action]');
      if (!button || !this.form.contains(button)) return;

      event.preventDefault();

      if (button.dataset.action === 'reset-guest') {
        this.guestIdInput.value = this.createGuestId();
        this.showStatus(this.dataset.textGuestReset, 'idle');
        return;
      }

      this.runAction(button.dataset.action, button);
    }

    async runAction(action, button) {
      try {
        const request = this.buildRequest(action);
        this.setBusy(true, button);
        this.showStatus(this.dataset.textRunning, 'running');

        const startedAt = performance.now();
        const response = await fetch(request.path, {
          method: request.method,
          credentials: 'same-origin',
          cache: 'no-store',
          headers: request.body ? { 'Content-Type': 'application/json' } : undefined,
          body: request.body ? JSON.stringify(request.body) : undefined,
        });
        const durationMs = Math.round(performance.now() - startedAt);
        const responseText = await response.text();
        let responseBody = responseText;

        try {
          responseBody = responseText ? JSON.parse(responseText) : null;
        } catch (_error) {
          // Keep non-JSON responses visible without interpreting them as HTML.
        }

        this.result.textContent = JSON.stringify({
          request: {
            method: request.method,
            path: request.path,
            body: request.body || null,
          },
          response: {
            status: response.status,
            status_text: response.statusText,
            ok: response.ok,
            duration_ms: durationMs,
            request_id: response.headers.get('x-request-id'),
            cache_control: response.headers.get('cache-control'),
            body: responseBody,
          },
        }, null, 2);

        this.showStatus(
          `${response.status} ${response.statusText} · ${durationMs} ms`,
          response.ok ? 'success' : 'error',
        );
      } catch (error) {
        this.result.textContent = JSON.stringify({
          error: error instanceof Error ? error.message : String(error),
        }, null, 2);
        this.showStatus(this.dataset.textRequestFailed, 'error');
      } finally {
        this.setBusy(false);
      }
    }

    buildRequest(action) {
      const productId = () => this.getShopifyId('product_id');
      const variantId = () => this.getShopifyId('variant_id');
      const guestId = () => this.getGuestIdValue();

      switch (action) {
        case 'proxy-health':
          return { method: 'GET', path: this.proxyPath };
        case 'unknown-route':
          return { method: 'GET', path: `${this.proxyPath}/v1/unknown` };
        case 'signals':
          return { method: 'GET', path: `${this.proxyPath}/v1/products/${productId()}/signals` };
        case 'view':
          return {
            method: 'POST',
            path: `${this.proxyPath}/v1/views`,
            body: { product_id: productId(), guest_id: guestId() },
          };
        case 'favorite-state': {
          const variantIds = this.getVariantIds();
          return {
            method: 'GET',
            path: this.withQuery(`${this.proxyPath}/v1/favorites/state`, { variant_ids: variantIds }),
          };
        }
        case 'favorite-list':
          return {
            method: 'GET',
            path: this.withQuery(`${this.proxyPath}/v1/favorites`, { page: '1', page_size: '12' }),
          };
        case 'favorite-add':
          return {
            method: 'PUT',
            path: `${this.proxyPath}/v1/favorites/${variantId()}`,
            body: { product_id: productId() },
          };
        case 'favorite-remove':
          return { method: 'DELETE', path: `${this.proxyPath}/v1/favorites/${variantId()}` };
        case 'identity-link':
          return {
            method: 'POST',
            path: `${this.proxyPath}/v1/identity/link`,
            body: { guest_id: guestId() },
          };
        default:
          throw new Error(this.dataset.textUnknownAction);
      }
    }

    getShopifyId(name) {
      const value = this.form.elements[name]?.value.trim() || '';
      if (!/^\d+$/.test(value)) throw new Error(this.dataset.textIdRequired);
      return value;
    }

    getVariantIds() {
      const value = this.form.elements.variant_ids?.value.trim() || '';
      if (!/^\d+(,\d+)*$/.test(value)) throw new Error(this.dataset.textVariantIdsRequired);
      return value;
    }

    getGuestIdValue() {
      const value = this.guestIdInput.value.trim();
      if (!/^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value)) {
        throw new Error(this.dataset.textGuestIdRequired);
      }
      return value;
    }

    getGuestId() {
      const storedId = window.localStorage.getItem('btSignalsTestGuestId');
      if (/^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(storedId || '')) {
        return storedId;
      }
      return this.createGuestId();
    }

    createGuestId() {
      const guestId = window.crypto.randomUUID();
      window.localStorage.setItem('btSignalsTestGuestId', guestId);
      return guestId;
    }

    withQuery(path, params) {
      const url = new URL(path, window.location.origin);
      Object.entries(params).forEach(([key, value]) => url.searchParams.set(key, value));
      return `${url.pathname}${url.search}`;
    }

    setBusy(isBusy, activeButton) {
      this.setAttribute('aria-busy', String(isBusy));
      this.querySelectorAll('button[data-action]').forEach((button) => {
        button.disabled = isBusy;
        button.classList.toggle('bt-signals-test__button--active', isBusy && button === activeButton);
      });
    }

    showStatus(text, state) {
      this.status.textContent = text;
      this.status.dataset.state = state;
    }
  }

  customElements.define('bt-signals-test', BtSignalsTest);
}